import { useRef, useState } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { mutations, queries } from '../../../graphql'
import {
  Box,
  Button,
  CardBox,
  Container,
  Fields,
  InputAdornment,
  NotificationDialog,
  Stack,
  Text,
  Scheduled,
  Recurring,
  Row,
  useAlert,
  validations,
} from '../../../components'
import { fromAmountField, getTrackingData } from '../../../utils'
import * as Card from '../../../components/payment/credit-card'

export function CreditCard({ addingFunds = true, closeForm }) {
  const { fundId } = useParams()
  const { data: { me: { fund: { id: mainFundId } = {} } = {} } = {} } = useQuery(queries.funds.myFund)
  const currentFundId = fundId || mainFundId
  const client = useApolloClient()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [dialogOpen, setDialogOpen] = useState(false)
  const cardApi = useRef()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors = {}, isSubmitting },
    setValue,
    getValues,
    reset: resetForm,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: 0,
      isRecurring: false,
      recurringInterval: 'monthly',
      multiUse: true,
    },
  })

  const closeSuccessDialog = () => {
    resetForm()
    setDialogOpen(false)
  }

  const onSubmit = async ({ multiUse, amount, paymentMethodId, isRecurring, recurringInterval, processingDate }) => {
    if (cardApi.current.isNewCard) {
      try {
        /* eslint-disable no-param-reassign */
        paymentMethodId = await cardApi.current.postToStripeAndSavePaymentMethod({ multiUse })
      } catch (e) {
        setAlert({
          message: `This card cannot be used to finalize the transaction: ${e.message || e.type}`,
          error: e.message,
          severity: 'error',
        })
        return
      }
    }

    if (cardApi.current.contribution) {
      try {
        await client.mutate({
          mutation: mutations.contributions.createContribution,
          variables: {
            data: {
              paymentMethodId,
              fundId: Number(currentFundId),
              amount: fromAmountField(String(amount)),
              isRecurring,
              recurringInterval,
              processingDate,
              ...getTrackingData(),
            },
          },
          refetchQueries: [
            { query: queries.funds.myFund, variables: { fundId: Number(currentFundId) } },
            { query: queries.funds.myFundActivity, variables: { fundId: Number(currentFundId) } },
            { query: queries.funds.myRecurring, variables: { fundId: Number(currentFundId) } },
            { query: queries.paymentMethods.myCreditCards, variables: { fundId: Number(currentFundId) } },
          ],
          awaitRefetchQueries: true,
        })
        setDialogOpen(true)
      } catch (e) {
        setAlert({
          message: `Failed to create a contribution: ${e.message ?? ''}`,
          error: e.message,
          severity: 'error',
        })
      }
    } else {
      setDialogOpen(true)
    }
  }

  return addingFunds ? (
    <>
      <Container maxWidth="lg">
        <Stack spacing={0}>
          <CardBox title="Credit Card">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <Fields.Text
                  label="Amount"
                  name="amount"
                  error={!!errors?.amount}
                  helperText={errors?.amount?.message}
                  InputProps={{
                    inputComponent: Fields.Amount,
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: {
                      setValue: (value) => setValue('amount', value),
                      getValue: () => getValues('amount'),
                      defaultValue: 0,
                    },
                  }}
                  {...register('amount', {
                    ...{ min: { value: 1, message: 'Must be at least 1' } },
                    ...validations.required,
                    ...validations.number,
                  })}
                />
                <Card.Select ref={cardApi} register={register} fundId={Number(currentFundId)} />
                <Scheduled
                  register={register}
                  errors={errors}
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                />
                <Recurring register={register} errors={errors} getValues={getValues} setValue={setValue} />
                <Card.FeesNotice />
                <Button disabled={isSubmitting} type="submit" sx={{ alignSelf: 'flex-end' }}>
                  Submit
                </Button>
                <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
              </Stack>
            </form>
          </CardBox>
        </Stack>
      </Container>
      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message="You have successfully created a contribution."
        actions={[{ label: 'Done', onClick: closeSuccessDialog, to: `/funds/${currentFundId}/activity/giving-wallet` }]}
      />
    </>
  ) : (
    <>
      <Box sx={{ padding: 4, width: '100%' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Card.Select
              ref={cardApi}
              register={register}
              lockedToNew
              contribution={false}
              fundId={Number(currentFundId)}
            />
            <Row sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={closeForm} sx={{ width: '10%' }}>
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit" sx={{ width: '10%' }}>
                Save
              </Button>
            </Row>
            <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
          </Stack>
        </form>
      </Box>
      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message="You have successfully added a credit card."
        actions={[{ label: 'Done', onClick: closeForm }]}
      />
    </>
  )
}
